<template>
  <div class="h3 mb-4">Номера</div>

  <template v-if="!isQuestionnaireSent">
    <div class="paragraph-s_h text-secondary">Выберите категорию номера для добавления контента</div>
    <div class="paragraph-s_h text-secondary mt-4">
      <span class="text-red">*</span> — поля обязательные для заполнения
    </div>
  </template>

  <no-rooms v-if="questionnaireAnswers && !questionnaireAnswers.roomCategories?.length" />

  <template v-else>
    <e-form v-if="!isQuestionnaireSent" ref="formRef" class="mt-10">
      <expand-card
        v-for="(room, index) in rooms"
        :key="index"
        :title="room.name || ''"
        :error="cardsWithError.includes(index)"
        ref="expandCardRefs"
        icon="room"
        class="mb-4"
      >
        <template #subtitle>
          <div class="d-flex flex-wrap align-center paragraph-s_h text-secondary">
            <b>{{ room.type }}</b>
            <span class="mx-2">|</span>
            Основных мест:&nbsp;<b>{{ room.basePlaces }}</b>
            <span class="mx-2">|</span>
            Дополнительных мест:&nbsp;<b>{{ room.additionalPlaces }}</b>
          </div>
        </template>

        <div>
          <e-select
            v-model="room.roomsCount"
            :items="dictionaries?.rooms.roomNumbers || []"
            :rules="[required()]"
            class="mb-4"
          >
            <template #label>
              <div>Количество комнат в номере<span class="text-red">*</span></div>
            </template>
          </e-select>

          <e-input
            v-model:number-value="room.roomArea"
            class="rooms-information__tiny-input"
            :rules="[required(), maxLength(3)]"
            :maska="'#'.repeat(4)"
          >
            <template #label>
              <div>Площадь номера (кв.м)<span class="text-red">*</span></div>
            </template>
          </e-input>

          <inputs-group title="Тип дополнительного места" class="mt-10">
            <combobox-with-list
              v-model="room.typeOfExtendedPlace"
              label="Добавить"
              :options="dictionaries?.rooms.extrabedTypes || []"
            />
          </inputs-group>

          <inputs-group title="Наличие кухни в номере" required class="mt-10">
            <e-radio-group v-model="room.kitchen" :items="kitchenOptions" inline :rules="[required()]" />
          </inputs-group>

          <inputs-group title="Оборудование в номере" required class="mt-10">
            <combobox-with-list
              v-model="room.equipment"
              label="Добавить"
              :options="dictionaries?.rooms.equipment || []"
              :rules="[required()]"
            />
          </inputs-group>

          <inputs-group title="Балкон" required class="mt-10">
            <combobox-with-list
              v-model="room.balcony"
              label="Добавить"
              :options="dictionaries?.rooms.balconyTypes || []"
              :rules="[required()]"
            />
          </inputs-group>

          <inputs-group title="Мебель в номере" required class="mt-10">
            <combobox-with-list
              v-model="room.furniture"
              label="Добавить"
              :options="dictionaries?.rooms.furniture || []"
              :rules="[required()]"
            />
          </inputs-group>

          <inputs-group title="Санузел" required class="mt-10">
            <combobox-with-list
              v-model="room.bathroom"
              label="Добавить"
              :options="dictionaries?.rooms.bathroomTypes || []"
              :rules="[required()]"
            />
          </inputs-group>

          <inputs-group title="Оборудование санузла" required class="mt-10">
            <combobox-with-list
              v-model="room.bathroomEquipment"
              label="Добавить"
              :options="dictionaries?.rooms.bathroomEquipment || []"
              :rules="[required()]"
            />
          </inputs-group>

          <inputs-group title="Сервис в номере" required class="mt-10">
            <combobox-with-list
              v-model="room.roomService"
              label="Добавить"
              :options="dictionaries?.rooms.services || []"
              :rules="[required()]"
            />
          </inputs-group>

          <inputs-group title="Вид из номера" class="mt-10">
            <combobox-with-list v-model="room.roomView" label="Добавить" :options="dictionaries?.rooms.views || []" />
          </inputs-group>

          <inputs-group title="Фотографии номера" class="mt-10">
            <template #subtitle>
              Загрузите фотографии или укажите ссылку на фотобанк.
              <div class="mt-2">Если вы уже загрузили фотографии, нет необходимости их дублировать.</div>
            </template>

            <e-alert
              v-if="formRef?.isDirty && !room.images.length && !room.imagesLink"
              variant="error"
              thin
              class="w-100"
            >
              <div class="paragraph-m_h font-500">Не загружены фотографии и не указана ссылка на фотобанк</div>
            </e-alert>

            <content-images-uploader
              :ref="addUploaderRef"
              v-model="room.images"
              v-model:images-link="room.imagesLink"
              v-model:attachments="room.attachments"
              :content-section="ContentSection.Rooms"
              :section-object-name="room.cid"
              @uploaded="saveIfHasChanges"
            />
          </inputs-group>
        </div>
      </expand-card>

      <div class="d-flex justify-space-between mt-10">
        <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
        <e-btn variant="primary" append-icon-src="arrowRight" :loading="isLoading" @click="onSubmit(true)">Далее</e-btn>
      </div>
    </e-form>

    <div v-else class="mt-10">
      <expand-card v-for="(room, index) in rooms" :key="index" :title="room.name || ''" class="mb-4">
        <template #subtitle>
          <div class="d-flex flex-wrap align-center paragraph-s_h text-secondary">
            <b>{{ room.type }}</b>
            <span class="mx-2">|</span>
            Основных мест:&nbsp;<b>{{ room.basePlaces }}</b>
            <span class="mx-2">|</span>
            Дополнительных мест:&nbsp;<b>{{ room.additionalPlaces }}</b>
          </div>
        </template>

        <template v-if="room.roomsCount">
          <property-item title="Количество комнат в номере">
            {{ room.roomsCount }}
          </property-item>
        </template>

        <template v-if="room.roomArea">
          <property-item title="Площадь номера (кв.м)" class="mt-4">
            {{ room.roomArea }}
          </property-item>
        </template>

        <template v-if="room.typeOfExtendedPlace.length">
          <div class="mt-10 mb-2 paragraph-m_h font-600">Тип дополнительного места</div>
          <e-chip v-for="type in room.typeOfExtendedPlace" :key="type" class="mt-2 mr-2">
            {{ type }}
          </e-chip>
        </template>

        <div class="mt-10 mb-4 paragraph-m_h font-600">Наличие кухни в номере</div>
        <e-radio-group v-model="room.kitchen" inline disabled :items="kitchenOptions" />

        <template v-if="room.equipment.length">
          <div class="mt-10 mb-2 paragraph-m_h font-600">Оборудование в номере</div>
          <e-chip v-for="equipment in room.equipment" :key="equipment" class="mt-2 mr-2">
            {{ equipment }}
          </e-chip>
        </template>

        <template v-if="room.balcony.length">
          <div class="mt-10 mb-2 paragraph-m_h font-600">Балкон</div>
          <e-chip v-for="balcony in room.balcony" :key="balcony" class="mt-2 mr-2">
            {{ balcony }}
          </e-chip>
        </template>

        <template v-if="room.furniture.length">
          <div class="mt-10 mb-2 paragraph-m_h font-600">Мебель в номере</div>
          <e-chip v-for="furniture in room.furniture" :key="furniture" class="mt-2 mr-2">
            {{ furniture }}
          </e-chip>
        </template>

        <template v-if="room.bathroom.length">
          <div class="mt-10 mb-2 paragraph-m_h font-600">Санузел</div>
          <e-chip v-for="bathroom in room.bathroom" :key="bathroom" class="mt-2 mr-2">
            {{ bathroom }}
          </e-chip>
        </template>

        <template v-if="room.bathroomEquipment.length">
          <div class="mt-10 mb-2 paragraph-m_h font-600">Оборудование санузла</div>
          <e-chip v-for="bathroomEquipment in room.bathroomEquipment" :key="bathroomEquipment" class="mt-2 mr-2">
            {{ bathroomEquipment }}
          </e-chip>
        </template>

        <template v-if="room.roomService.length">
          <div class="mt-10 mb-2 paragraph-m_h font-600">Сервис в номере</div>
          <e-chip v-for="roomService in room.roomService" :key="roomService" class="mt-2 mr-2">
            {{ roomService }}
          </e-chip>
        </template>

        <template v-if="room.roomView.length">
          <div class="mt-10 mb-2 paragraph-m_h font-600">Вид из номера</div>
          <e-chip v-for="roomView in room.roomView" :key="roomView" class="mt-2 mr-2">
            {{ roomView }}
          </e-chip>
        </template>

        <div v-if="room.images.length || room.imagesLink" class="mt-10 paragraph-m_h font-600">Фотографии номера</div>

        <images-gallery v-if="room.images.length" class="mt-4" :files="room.images" />

        <template v-if="room.imagesLink">
          <property-item title="Ссылка на фотобанк" class="mt-4">
            {{ room.imagesLink }}
          </property-item>
        </template>
      </expand-card>

      <div class="d-flex justify-space-between mt-10">
        <e-btn variant="secondary" prepend-icon-src="arrowLeft" @click="back">Назад</e-btn>
        <e-btn variant="primary" append-icon-src="arrowRight" :loading="isLoading" @click="nextStep">Далее</e-btn>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { type ComponentPublicInstance, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue';
import type { EFormType } from '@/core/types/components/components';
import type { LoadedFile } from '@/core/types/files';
import ComboboxWithList from '@/core/components/interface/ComboboxWithList.vue';
import ExpandCard from '@/core/components/interface/ExpandCard.vue';
import InputsGroup from '@/core/components/interface/InputsGroup.vue';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import { EAlert, EBtn, EChip, EForm, EInput, ERadioGroup, ESelect } from '@/core/components/ui';
import ImagesGallery from '@/core/components/ui/files/filesGallery.vue';
import { elementErrorSelector } from '@/core/consts/components';
import { compareObjects } from '@/core/utils/objects/get-delta';
import { sleep } from '@/core/utils/sleep';
import { maxLength, required } from '@/core/utils/validation';
import { type Attachment, QuestionnaireMasterStep } from '@/graphql/generated';
import ContentImagesUploader from '@/parts/content/components/ContentImagesUploader.vue';
import NoRooms from '@/parts/content/components/NoRooms.vue';
import { useContentStore } from '@/parts/content/store';
import { ContentSection } from '@/parts/content/types';
import { useContentImagesUpload } from '@/parts/content/utils';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const { accommodationObject } = useAccommodationObjectsStore();
const {
  questionnaireAnswers,
  dictionaries,
  back,
  saveQuestionnaire,
  saveQuestionnaireDebounced,
  moveToNextStep,
  isQuestionnaireSent,
  nextStep,
  needValidate,
  completeValidation,
  questionnaireStatus,
} = useContentStore();

const { initImages } = useContentImagesUpload(toRef(() => accommodationObject.value?.cid));

const uploaderRefs = ref<any[]>([]);

const rooms = ref<
  {
    cid: string;
    name: string;
    type: string;
    basePlaces: number;
    additionalPlaces: number;
    typeOfExtendedPlace: string[];
    roomsCount: string | null;
    kitchen: string;
    balcony: string[];
    roomArea: number;
    equipment: string[];
    furniture: string[];
    bathroom: string[];
    bathroomEquipment: string[];
    roomService: string[];
    roomView: string[];
    attachments: Attachment[];
    images: LoadedFile[];
    imagesLink: string;
  }[]
>([]);

const init = () => {
  if (!questionnaireAnswers.value?.roomCategories) {
    return;
  }

  rooms.value = questionnaireAnswers.value.roomCategories.map((room) => ({
    cid: room.roomCategoryCid,
    name: room.roomCategoryName || '',
    type: room.roomCategoryTypeName || '',
    basePlaces: room.roomCategoryBasePlaces,
    additionalPlaces: room.roomCategoryExtPlaces,
    typeOfExtendedPlace: room.typeOfExtendedPlace || [],
    roomsCount: room.roomsCount || null,
    kitchen: room.kitchenIsInRoom || '',
    balcony: room.balcony || [],
    roomArea: room.roomArea,
    equipment: room.roomEquipment || [],
    furniture: room.furniture || [],
    bathroom: room.bathroom || [],
    bathroomEquipment: room.bathroomEquipment || [],
    roomService: room.roomService || [],
    roomView: room.roomView || [],
    attachments: room.attachments || [],
    images: [] as LoadedFile[],
    imagesLink: room.photobankUrl || '',
  }));

  rooms.value.forEach((room, roomIndex: number) => {
    void initImages(room.attachments, room.images, ContentSection.Rooms, rooms.value[roomIndex].cid);
  });
};

init();

const expandCardRefs = ref<ComponentPublicInstance[]>([]);
const cardsWithError = ref<number[]>([]);
const formRef = ref<EFormType>();
const isLoading = ref(false);

const addUploaderRef = (el: any) => {
  if (el && !uploaderRefs.value.includes(el)) {
    uploaderRefs.value.push(el);
  }
};

const save = () => saveQuestionnaireDebounced(mapAnswersToSave());
const saveIfHasChanges = async () => {
  if (hasChanges()) {
    await saveQuestionnaire(mapAnswersToSave());
  }
};

watch(
  rooms,
  () => {
    void save();
  },
  {
    deep: true,
  },
);

watch(questionnaireStatus, () => {
  if (questionnaireStatus.value) {
    init();
  }
});

const validate = async () => {
  await sleep(0);
  cardsWithError.value = [];

  const isFormValid = await formRef.value?.validate();

  expandCardRefs.value.forEach((card, index) => {
    const error = card.$el.querySelector(elementErrorSelector);
    if (error && !cardsWithError.value.includes(index)) {
      cardsWithError.value.push(index);
    }
  });

  if (!isFormValid || cardsWithError.value.length > 0) {
    if (cardsWithError.value.length > 0) {
      const firstErrorIndex = cardsWithError.value[0];
      expandCardRefs.value[firstErrorIndex]?.$el?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }
    return false;
  }

  for (const uploaderRef of uploaderRefs.value) {
    if (uploaderRef && !uploaderRef.validate()) {
      return false;
    }
  }

  return true;
};

const hasChanges = () => {
  if (questionnaireAnswers.value?.roomCategories) {
    questionnaireAnswers.value.roomCategories.forEach((room) => {
      delete room.__typename;
    });

    if (!compareObjects(mapAnswersToSave().roomCategories, questionnaireAnswers.value.roomCategories)) {
      return true;
    }
  }

  return false;
};

const onSubmit = async (force = false) => {
  if (!force && !hasChanges()) {
    return true;
  }

  cardsWithError.value = [];

  const isValid = await validate();
  if (!isValid || cardsWithError.value.length) {
    return false;
  }

  isLoading.value = true;
  await moveToNextStep(QuestionnaireMasterStep.RoomCategories, mapAnswersToSave());
  isLoading.value = false;

  return true;
};

const kitchenOptions = [
  {
    title: 'Да',
    value: 'Да',
  },
  {
    title: 'Нет',
    value: 'Нет',
  },
  {
    title: 'Миникухня',
    value: 'Миникухня',
  },
];

onMounted(async () => {
  if (needValidate.value) {
    await validate();
    await completeValidation();
  }
});

onBeforeUnmount(async () => {
  await saveIfHasChanges();
});

function mapAnswersToSave() {
  return {
    roomCategories: rooms.value.map((room) => ({
      attachments: room.attachments,
      balcony: room.balcony,
      bathroom: room.bathroom,
      bathroomEquipment: room.bathroomEquipment,
      furniture: room.furniture,
      kitchenIsInRoom: room.kitchen,
      photobankUrl: room.imagesLink,
      roomArea: room.roomArea,
      roomCategoryBasePlaces: room.basePlaces,
      roomCategoryExtPlaces: room.additionalPlaces,
      roomCategoryCid: room.cid,
      roomCategoryName: room.name,
      roomCategoryTypeName: room.type,
      roomEquipment: room.equipment,
      roomService: room.roomService,
      roomView: room.roomView,
      roomsCount: room.roomsCount,
      typeOfExtendedPlace: room.typeOfExtendedPlace,
    })),
  };
}
</script>

<style lang="scss" scoped>
.rooms-information {
  &__tiny-input {
    width: 100%;
    max-width: 280px;
  }
}
</style>
